import { emit } from "infrastructure/eventHub";

export class BaseRepository {
  constructor(db, storeName) {
    this.db = db;
    this.storeName = storeName;
    this.memoryCache = {};

    this._emitScheduled = false;
  }

  async tx(mode, fn) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(this.storeName, mode);
      const store = tx.objectStore(this.storeName);
      const result = fn(store);

      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async getFromCache(scope, key = "(all)", fnToGet) {
    if(!Object.hasOwn(this.memoryCache, scope)) {
      this.memoryCache[scope] = {};
    }

    if (!Object.hasOwn(this.memoryCache[scope], key)) {
      this.memoryCache[scope][key] = {};
    }

    if (Object.hasOwn(this.memoryCache[scope][key], "value")) {
      return this.memoryCache[scope][key].value;
    }

    if (typeof fnToGet !== "function") {
      return null;
    }

    const value = await fnToGet();
    this.memoryCache[scope][key].value = value;
    return value;
  }

  clearCache(scope, key) {
    if (key === undefined && Object.hasOwn(this.memoryCache, scope)) {
      this.memoryCache[scope] = {};
    } else {
      if (Object.hasOwn(this.memoryCache, scope) && Object.hasOwn(this.memoryCache[scope], key)) {
        delete this.memoryCache[scope][key];
      }
    }
  }

  _emitChange() {
    if (this._emitScheduled) return;

    this._emitScheduled = true;

    queueMicrotask(() => {
      this._emitScheduled = false;
      emit("repository", this.storeName, "updated", "both");
    });
  }

  where(indexName) {
    return {
      eq: (value) =>
        new Promise((resolve, reject) => {
          const tx = this.db.transaction(this.storeName, "readonly");
          const store = tx.objectStore(this.storeName);
          const index = store.index(indexName);

          const req = index.getAll(value);
          req.onsuccess = () => resolve(req.result || []);
          req.onerror = () => reject(req.error);
        }),
    };
  }

  // Operaciones primitivas

  _getById(store, id) {
    return new Promise((resolve, reject) => {
      const req = store.get(id);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result ?? null);
    });
  }

  getById(id) {
    return this.tx("readonly", store => {
      return this._getById(store, id);
    });
  }

  _getAll(store) {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result ?? []);
    });
  }

  getAll() {
    return this.tx("readonly", store => {
      return this._getAll(store);
    });
  }

  _upsert(store, item) {
    store.put({
      ...item
    });
  }

  // Crea o actualiza el item completo (sin merge)
  upsert(item) {
    return this.tx("readwrite", store => {
      this._upsert(store, item);
    }).then(() => {
      this._emitChange();
    });
  }

  upsertMany(items = []) {
    return this.tx("readwrite", store => {
      items.forEach(item => this._upsert(store, item));
    }).then(() => {
      this._emitChange();
    });
  }

  _delete(store, id) {
    store.delete(id);
  }

  delete(id) {
    return this.tx("readwrite", store => {
      this._delete(store, id);
    }).then(() => {
      this._emitChange();
    });
  }

  deleteMany(ids = []) {
    return this.tx("readwrite", store => {
      ids.forEach(id => this._delete(store, id));
    }).then(() => {
      this._emitChange();
    });
  }

  _patch(store, id, changes) {
    return new Promise((resolve, reject) => {
      const req = store.get(id);
      req.onerror = () => reject(req.error);

      req.onsuccess = () => {
        if (!req.result) {
          resolve(null);
          return;
        }

        if (typeof changes === "function") {
          changes = changes(req.result);
        }

        store.put({
          ...req.result,
          ...changes
        });

        resolve(true);
      };
    });
  }

  // Actualiza parcialmente un item (si no existe, no hace nada)
  patch(id, changes) {
    return this.tx("readwrite", store => {
      return this._patch(store, id, changes);
    }).then(() => {
      this._emitChange();
    });
  }

  patchMany(patches = []) {
    return this.tx("readwrite", store => {
      return Promise.all(
        patches.map(p => this._patch(store, p.id, p.changes))
      );
    }).then(() => {
      this._emitChange();
    });
  }

  // Crea o actualiza un item haciendo merge con el existente
  async upsertMerge(item) {
    const existing = await this.getById(item.id);

    if (!existing) {
      return this.upsert(item); // emite
    }

    return this.patch(item.id, item); // emite
  }

  upsertMergeMany(items = []) {
    return this.tx("readwrite", store => {
      items.forEach(item => {
        const req = store.get(item.id);

        req.onsuccess = () => {
          if (!req.result) {
            this._upsert(store, item);
          } else {
            this._patch(store, item.id, item);
          }
        };
      });
    }).then(() => {
      this._emitChange();
    });
  }

  async mirror(remoteItems = [], {
    idField = "id",
    scopeIndex,
    scopeValue,
    scopeFilter
    } = {}
  ) {
    return this.tx("readwrite", async store => {
      let local;

      if (scopeIndex) {
        const index = store.index(scopeIndex);
        local = await new Promise((resolve, reject) => {
          const req = index.getAll(scopeValue);
          req.onsuccess = () => resolve(req.result || []);
          req.onerror = () => reject(req.error);
        });
      } else {
        local = await this._getAll(store);
      }

      if (scopeFilter && Array.isArray(local)) {
        local = scopeFilter(local);
      }

      const remoteIds = new Set(remoteItems.map(i => i[idField]));

      local.forEach(item => {
        if (!remoteIds.has(item[idField])) {
          this._delete(store, item[idField]);
        }
      });

      remoteItems.forEach(item => {
        this._upsert(store, item);
      });
    }).then(() => {
      this._emitChange();
    });
  }

  replaceByKey({ index, value, newItem }) {
    return this.tx("readwrite", (store) => {
      return new Promise((resolve, reject) => {
        const idx = store.index(index);
        const req = idx.get(value);

        req.onerror = () => reject(req.error);

        req.onsuccess = () => {
          const existing = req.result;

          const merged = {
            ...(existing || {}),
            ...newItem
          };

          if (existing) {
            store.delete(existing.id);
          }

          store.put(merged);
          resolve(merged);
        };
      });
    }).then(() => {
      this._emitChange();
    });
  }

}
